import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useUserContext } from '../context/UserContext';
import { LoginModalProvider } from '../context/LoginModalContext';
import { SharedFavoritesResponse, useGetSharedFavorites } from '../api/favorites';
import { useAnalytics } from '../context/AnalyticsContext';
import Header from '../components/Header';
import CampCard from '../components/camps/CampCard';
import './SharedFavoritesInvite.css';
import { Heart } from 'lucide-react';
import RegisterForm from '../components/login/RegisterForm';
import LoginModal from '../components/login/LoginModal';
import { useNotifications } from '../context/NotificationContext';

const SharedFavoritesInvite: React.FC = () => {
  const { isLoggedIn } = useUserContext();  
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [sharedFavorites, setSharedFavorites] = useState<SharedFavoritesResponse>({ camps: [], uuid: "", shared_by: "" });
  const { showSuccess } = useNotifications();
  const location = useLocation();
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const { favoriteUuid } = useParams<{ favoriteUuid: string }>();
  const shareId = queryParams.get("share_id");
  const { data, isLoading } = useGetSharedFavorites(
    favoriteUuid ?? "",
    shareId ?? "",
    { enabled: isLoggedIn && Boolean(favoriteUuid) && Boolean(shareId) }
  );
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    trackPageView('/favorites/invite', 'Shared Favorites Invite');
  }, [trackPageView]);

  useEffect(() => {
    if (data?.camps) {
      setSharedFavorites(data);
    }
  }, [data, setSharedFavorites, isLoggedIn, favoriteUuid, shareId]);

  const handleDateChange = (campId: string, dateId: string) => {
    // noop
  };

  const handleRegister = (email: string) => {
    showSuccess("You have successfully registered! You can now view the shared favorite camps list as well as create your own favorites.")
  };

  const getSubtitleText = () => {
    if (isLoading) {
      return 'Loading shared camps...';
    }
    if (sharedFavorites?.shared_by && sharedFavorites.camps.length > 0) {
      return `${sharedFavorites.shared_by} has shared ${sharedFavorites.camps.length} camp${sharedFavorites.camps.length !== 1 ? 's' : ''} with you.`;
    }
    if (sharedFavorites?.camps.length) {
      return `${sharedFavorites.camps.length} camp${sharedFavorites.camps.length !== 1 ? 's' : ''} shared with you.`;
    }
    return 'No camps in this shared list.';
  };

  if (!isLoggedIn) {
    return (
      <main className="shared-invite-main">
        <div className="shared-invite-container">
          <div className="shared-invite-card">
            <div className="shared-invite-icon">
              <Heart size={64} strokeWidth={1.5} fill="var(--primary-color)" color="var(--primary-color)" />
            </div>
            
            <h1 className="shared-invite-title">Welcome to Aika!</h1>
            <p className="shared-invite-description">
              Someone you know has shared a curated list of holiday camps they think you'd love. 
              Create a free account to view this personalized selection and find the perfect camp for your child.
            </p>
            <LoginModalProvider initialStep="register">
              <RegisterForm onRegister={handleRegister} onClose={() => {}} />
              <p className="login-link">
                Already have an account? <button 
                  type="button" 
                  className="login-link-text" 
                  onClick={() => setShowLoginModal(true)}
                >
                  Sign in
                </button>
              </p>
            </LoginModalProvider>

            <div className="shared-invite-actions">
              <button 
                className="btn btn--secondary btn--large"
                onClick={() => navigate('/')}
              >
                Learn More About Us
              </button>
            </div>

            <div className="shared-invite-info">
              <p className="shared-invite-info-text">
                <strong>What you'll discover:</strong>
              </p>
              <ul className="shared-invite-info-list">
                <li>Quick and easy sign-up - completely free</li>
                <li>Access the shared collection of camps</li>
                <li>Explore hundreds of verified holiday camps</li>
                <li>Save favorites and plan your child's summer</li>
              </ul>
            </div>
          </div>
        </div>
        {/* Render LoginModal when needed */}
        {showLoginModal && (
          <LoginModalProvider initialStep="email">
            <LoginModal onClose={() => setShowLoginModal(false)} onLogin={() => {}} onRegister={() => {}} />
          </LoginModalProvider>
        )}
      </main>
    );
  }

  return (
    <div className="shared-favorites-page">
      <Header />
      <main className="shared-favorites-main">
        <div className="shared-favorites-container">
          <div className="shared-favorites-header">
            <div className="shared-favorites-title-section">
              <p className="shared-favorites-subtitle">
                {getSubtitleText()}
              </p>
            </div>
          </div>

          {isLoading && (
            <div className="shared-favorites-loading">
              <div className="loading-spinner"></div>
              <p>Loading shared camps...</p>
            </div>
          )}

          {!isLoading && sharedFavorites && sharedFavorites.camps.length > 0 && (
            <div className="shared-favorites-grid">
              {sharedFavorites.camps.map((camp) => (
                <CampCard 
                  key={camp.id} 
                  camp={camp}
                  favoriteId={favoriteUuid}
                  selectedDate={undefined}
                  onDateChange={handleDateChange}
                  scheduledDateIds={undefined}
                />
              ))}
            </div>
          )}

          {!isLoading && (!sharedFavorites || sharedFavorites.camps.length === 0) && (
            <div className="shared-favorites-empty">
              <div className="empty-state">
                <div className="empty-icon">
                  <Heart size={64} strokeWidth={1.5} />
                </div>
                <h2>No Camps Found</h2>
                <p>This shared favorites list is empty or the link may have expired.</p>
                <button 
                  className="btn btn--primary"
                  onClick={() => navigate('/search')}
                >
                  Browse All Camps
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>



  );
};

export default SharedFavoritesInvite;
function showSuccess(arg0: string) {
  throw new Error('Function not implemented.');
}

